'use client'

import { AreaItem } from '@/types/cms/AreaItem'
import styles from './TeaserPreview.module.scss'
import TeaserHighlight_1 from '@/elements/widgets/Teaser/Highlight/_1/TeaserHighlight_1'
import SectionTitle from '@/components/SectionTitle/SectionTitle'
import TeaserHighlight_2 from '@/elements/widgets/Teaser/Highlight/_2/TeaserHighlight_2'
import TeaserHighlight_3 from '@/elements/widgets/Teaser/Highlight/_3/TeaserHighlight_3'
import TeaserSmall from '@/elements/widgets/Teaser/Small/TeaserSmall'
import AppContext from '@/types/context'
import TeaserSmallInline from '@/elements/widgets/Teaser/Small/TeaserSmallInline'
import TeaserInfinity from '@/elements/widgets/Teaser/Infinity/TeaserInfinity'
import TeaserInline from '@/elements/widgets/Teaser/Inline/TeaserInline'
import TeaserSlider from '@/elements/widgets/Teaser/Slider/TeaserSlider'
import Switch from '@/components/Switch/Switch'
import { ReactNode, useEffect, useState } from 'react'
import MultiSelect from '@/components/MultiSelect/MultiSelect'
import MobilePreview from '@/components/MobilePreview/MobilePreview'

const TeaserPreview = ({ item, context }: { item: AreaItem; context: AppContext }) => {
  const [controls, setControls] = useState({
    hideLeadText: false,
    hideSection: false,
    mobile: false,
  })
  const [itemState, setItemState] = useState(item)
  const VARIANTS = [
    'Highlight_1',
    'Highlight_2',
    'Highlight_3',
    'Infinity',
    'Inline',
    'Slider',
    'Small',
    'Small Inline',
  ]
  const [selectedVariants, setSelectedVariants] = useState(['Highlight_2', 'Small'])

  if (!item) {
    return null
  }

  const overrideImageCrop = (item: AreaItem, targetCrop: string) => {
    let newItem = JSON.parse(JSON.stringify(item))

    if (newItem?.items && newItem.items[0]?.model?.image) {
      newItem.items[0].model.image.crop = targetCrop
    }
    return newItem
  }

  const duplicate = (item: AreaItem, count: number) => {
    let newItem = JSON.parse(JSON.stringify(item))
    if (newItem?.items) {
      newItem.items = Array(count).fill(newItem.items[0])
    }
    return newItem
  }

  const handleControlChange = (value: boolean, key: string) => {
    setControls((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  useEffect(() => {
    setItemState((prev) => ({
      ...prev,
      model: {
        fields: {
          hideLeadText: controls.hideLeadText.valueOf(),
          hideSection: controls.hideSection.valueOf(),
          mobile: controls.mobile.valueOf(),
        },
      },
    }))
  }, [controls])

  const PreviewScreen = ({ children }: { children: ReactNode }) => {
    if (controls.mobile) {
      return <MobilePreview>{children}</MobilePreview>
    }
    return <>{children}</>
  }

  return (
    <>
      <hr style={{ width: '100%', height: '1px' }} />
      <div className={styles.container} data-cy="teaser-preview">
        <SectionTitle title={'TEASER-PREVIEW'} />
        <h1 data-cy="teaser-preview_controls-title">Controls</h1>
        <div className={styles['control-container']}>
          <div className={styles.control} data-cy="teaser-preview_control-hide-lead-text">
            <span className={styles.label}>Hide lead text</span>
            <Switch onClick={(value) => handleControlChange(value, 'hideLeadText')} />
          </div>
          <div className={styles.control} data-cy="teaser-preview_control-hide-section">
            <span className={styles.label}>Hide section</span>
            <Switch onClick={(value) => handleControlChange(value, 'hideSection')} />
          </div>
          <div className={styles.control} data-cy="teaser-preview_control-teaser-variants">
            <span className={styles.label}>Teaser variants</span>
            <MultiSelect options={VARIANTS} selected={selectedVariants} onChange={setSelectedVariants} />
          </div>
          <div className={styles.control} data-cy="teaser-preview_control-mobile">
            <span className={styles.label}>Mobile preview</span>
            <Switch onClick={(value) => handleControlChange(value, 'mobile')} />
          </div>
        </div>
        <PreviewScreen>
          {selectedVariants.includes('Highlight_1') && (
            <div className={styles['preview-section']} data-cy="teaser-preview_section-highlight-1">
              <h2>Highlight_1</h2>
              <TeaserHighlight_1 item={overrideImageCrop(itemState, 'Img_17_6')} />
            </div>
          )}
          {selectedVariants.includes('Highlight_2') && (
            <div className={styles['preview-section']} data-cy="teaser-preview_section-highlight-2">
              <h2>Highlight_2</h2>
              <TeaserHighlight_2 item={overrideImageCrop(itemState, 'Img_17_6')} />
            </div>
          )}
          {selectedVariants.includes('Highlight_3') && (
            <div className={styles['preview-section']} data-cy="teaser-preview_section-highlight-3">
              <h2>Highlight_3</h2>
              <TeaserHighlight_3 item={overrideImageCrop(itemState, 'Img_17_6')} />
            </div>
          )}
          {selectedVariants.includes('Infinity') && (
            <div className={styles['preview-section']} data-cy="teaser-preview_section-infinity">
              <h2>Infinity</h2>
              <TeaserInfinity item={duplicate(overrideImageCrop(itemState, 'Img_16_9'), 21)} />
            </div>
          )}

          {selectedVariants.includes('Inline') && (
            <div className={styles['preview-section']} data-cy="teaser-preview_section-inline">
              <h2>Inline</h2>
              <TeaserInline item={overrideImageCrop(itemState, 'Img_16_9')} context={context} />
            </div>
          )}
          {selectedVariants.includes('Slider') && (
            <div className={styles['preview-section']} data-cy="teaser-preview_section-slider">
              <h2>Slider</h2>
              <TeaserSlider item={duplicate(itemState, 20)} />
            </div>
          )}
          {selectedVariants.includes('Small') && (
            <div className={styles['preview-section']} data-cy="teaser-preview_section-small">
              <h2>Small</h2>
              <TeaserSmall item={overrideImageCrop(itemState, 'Img_1_1')} context={context} />
            </div>
          )}
          {selectedVariants.includes('Small Inline') && (
            <div className={styles['preview-section']} data-cy="teaser-preview_section-small-inline">
              <h2>Small Inline</h2>
              <TeaserSmallInline item={overrideImageCrop(itemState, 'Img_1_1')} context={context} />
            </div>
          )}
        </PreviewScreen>
      </div>
    </>
  )
}

export default TeaserPreview
